// Payments are credited to people by payment_details.paid_to (who the money is for),
// never by payments.paid_by / paid_by_role (who paid) — the stored role can be wrong,
// e.g. an organiser paying for a guest saved as 'invitee'. Everything that decides
// whether someone has paid should go through these helpers.

// Every completed payment on the event, whoever made it. lead.payments, each invitee's
// payments and event.payments overlap, so it's deduped by id, and only this event's are kept. event.payments (all
// payments on the event) is what catches ones the payer-based relations miss; older
// payloads without it fall back to lead + invitee payments.
export function eventPayments(event, lead = null) {
    const seen = new Set()
    return [
        ...((event.lead || lead)?.payments || []),
        ...(event.invitees || []).flatMap(inv => inv.payments || []),
        ...(event.payments || []),
    ].filter(p => {
        if (p.payment_collection == null || seen.has(p.id)) return false
        // The lead/invitee payment relations aren't event-scoped and an invitee's id can equal
        // another event's lead id, so drop payments that belong to a different event
        if (event.id != null && p.event_id != null && p.event_id != event.id) return false
        seen.add(p.id)
        return true
    })
}

// Non-charge payment_details assigned to one person (lead or invitee id).
export function detailsAssignedTo(event, personId, lead = null) {
    return eventPayments(event, lead).flatMap(p =>
        (p.payment_details || []).filter(d => d.paid_to == personId && !d.is_charge)
    )
}

// Current per-person price of the package someone is attached to, so on an event with several
// packages each person is checked against their own: the invitee's quote_id (the organiser's
// lead.quote_id), else the quote on their Full Payment, else the event's only quote.
// null when the event has several packages and none of those says which is theirs (no guessing);
// 0 when there's no price to check at all.
export function personPackagePrice(event, personId, lead = null) {
    if ((event.quotes || []).length === 0) return 0
    const quote = personPackageQuote(event, personId, lead)
    return quote ? (Number(quote.total) || 0) : null
}

// The booked package (quote) someone is attached to, by the rule above, or null.
export function personPackageQuote(event, personId, lead = null) {
    const quotes = event.quotes || []
    if (quotes.length === 0) return null
    const isLead = personId == (event.lead || lead)?.id
    const person = isLead ? (event.lead || lead) : (event.invitees || []).find(inv => inv.id == personId)
    const fullPayment = detailsAssignedTo(event, personId, lead).find(d => d.payment_type_id == 1 && Number(d.quote_id) > 0)
    return quotes.find(q => Number(person?.quote_id) > 0 && q.id == person.quote_id)
        || quotes.find(q => q.id == fullPayment?.quote_id)
        || (quotes.length === 1 ? quotes[0] : null)
}

// Minimum group size check per booked package that has one (quotes.min_groupsize, set in the
// Custom Quote editor): [{ quote_id, name, minimum, paid, short }]. paid = people on that
// package (organiser + invitees, by personPackageQuote) who have paid it in full (status
// 'Paid in Full'; deposits and Balance Due don't count); short = how many more need to pay, 0 when the minimum is met. People
// whose package can't be told apart on a multi-package event aren't counted. See
// docs/reference/MINIMUM_GROUP_SIZE.md.
export function eventMinimumChecks(event) {
    const quotes = (event?.quotes || []).filter(q => Number(q.min_groupsize) > 0)
    if (quotes.length === 0) return []
    const paidByQuote = {}
    const people = [event.lead?.id, ...(event.invitees || []).map(inv => inv.id)].filter(id => id != null)
    people.forEach(id => {
        if (personPaymentStatus(event, id) !== 'Paid in Full') return
        const quote = personPackageQuote(event, id)
        if (quote) paidByQuote[quote.id] = (paidByQuote[quote.id] || 0) + 1
    })
    return quotes.map(q => {
        const minimum = Number(q.min_groupsize)
        const paid = paidByQuote[q.id] || 0
        return { quote_id: q.id, name: q.name, minimum, paid, short: Math.max(0, minimum - paid) }
    })
}

// What one person (lead or invitee id) has paid against their package, as the Manage
// Invitees table shows it:
// - status: 'Paid in Full' once everything assigned to them (deposits, full payments,
//   contributions) covers the package's current price; 'Balance Due' when they made a full
//   payment that no longer covers it (the quote was edited after they paid); else
//   'Paid Deposit' (something paid) or 'Unpaid'. A £0 Full Payment is Mark as Paid
//   (No Payment), a deliberate comp, so always Paid in Full. With no price to check, any
//   Full Payment or contribution is Paid in Full, as before.
// - mismatch: they paid in full (or over) but the total isn't the current price — the
//   flag on the invitees table. difference = paid - price (negative = still owed).
export function personPaymentSummary(event, personId, lead = null) {
    const details = detailsAssignedTo(event, personId, lead)
    const paid = Math.round(details.reduce((sum, d) => sum + Number(d.amount), 0) * 100) / 100
    const price = personPackagePrice(event, personId, lead)
    const hasFullPayment = details.some(d => d.payment_type_id == 1)
    const waived = details.some(d => d.payment_type_id == 1 && Number(d.amount) === 0)
    const difference = price === null ? 0 : Math.round((paid - price) * 100) / 100

    let status
    if (waived) status = 'Paid in Full'
    // Package unknown on a multi-package event: a full payment still counts, deposits and
    // contributions alone never do, and nothing is flagged
    else if (price === null) status = hasFullPayment ? 'Paid in Full' : (paid > 0 ? 'Paid Deposit' : 'Unpaid')
    else if (price === 0) status = (hasFullPayment || details.some(d => d.payment_type_id == 6)) ? 'Paid in Full' : (paid > 0 ? 'Paid Deposit' : 'Unpaid')
    else if (difference >= 0) status = 'Paid in Full'
    else if (hasFullPayment) status = 'Balance Due'
    else status = paid > 0 ? 'Paid Deposit' : 'Unpaid'

    const mismatch = price > 0 && !waived && difference !== 0 && (hasFullPayment || difference > 0)
    return { status, paid, price, difference, mismatch }
}

// 'Paid in Full' | 'Balance Due' | 'Paid Deposit' | 'Unpaid' (see personPaymentSummary).
export function personPaymentStatus(event, personId, lead = null) {
    return personPaymentSummary(event, personId, lead).status
}

// The invitee staff set as Stag/Hen (events.stag_hen_invitee_id), else the first invitee
// (the placeholder created with the event). Same rule in ggb_client_app (modules/invitee/stag_hen.ts).
export function stagHenInviteeId(event) {
    const invitees = event?.invitees || []
    if (invitees.length === 0) return null
    const chosen = event.stag_hen_invitee_id
    if (chosen && invitees.some(inv => inv.id === chosen)) return chosen
    return Math.min(...invitees.map(inv => inv.id))
}

// Dates for the table's "Date of Payment" column and the payment summary email. Paid in Full is
// their first Full Payment, or once deposits/contributions alone cover the package, the payment
// that covered it. Deposit is their first Deposit, or their first contribution while it's
// not covered.
export function personPaymentDates(event, personId, lead = null) {
    const { status, price } = personPaymentSummary(event, personId, lead)
    let fullPaymentDate = null, depositDate = null, contributionDate = null, coveredDate = null
    let runningTotal = 0
    const payments = eventPayments(event, lead)
        .slice()
        .sort((a, b) => new Date(a.payment_date) - new Date(b.payment_date))
    for (const p of payments) {
        for (const d of (p.payment_details || [])) {
            if (d.paid_to != personId || d.is_charge) continue
            if (d.payment_type_id == 1 && !fullPaymentDate) fullPaymentDate = p.payment_date
            if (d.payment_type_id == 2 && !depositDate) depositDate = p.payment_date
            if (d.payment_type_id == 6 && !contributionDate) contributionDate = p.payment_date
            runningTotal += Number(d.amount)
            if (!coveredDate && price > 0 && runningTotal >= price - 0.005) coveredDate = p.payment_date
        }
    }
    if (!fullPaymentDate && status === 'Paid in Full') fullPaymentDate = coveredDate || contributionDate
    if (!fullPaymentDate && !depositDate && contributionDate) depositDate = contributionDate
    return { fullPaymentDate, depositDate }
}

// Name of the package someone paid in full for (their first Full Payment's quote), else null.
export function personPackageName(event, personId, lead = null) {
    const detail = detailsAssignedTo(event, personId, lead).find(d => d.payment_type_id == 1)
    if (!detail) return null
    const quote = (event.quotes || []).find(q => q.id == detail.quote_id) || detail.quote
    return quote?.name || null
}

// An invitee's admin fee (its own charge line, type 8, the real amount charged), counted only
// once they've paid in full, never on a deposit. Never the organiser's.
export function personAdminFee(event, personId, lead = null) {
    if (personId == (event.lead || lead)?.id) return 0
    const assigned = eventPayments(event, lead).flatMap(p => (p.payment_details || []).filter(d => d.paid_to == personId))
    if (!assigned.some(d => !d.is_charge && d.payment_type_id == 1)) return 0
    return assigned.filter(d => d.is_charge && d.payment_type_id == 8).reduce((sum, d) => sum + Number(d.amount), 0)
}

// Total paid shown per person: everything assigned to them plus their admin fee (personAdminFee).
export function personTotalPaid(event, personId, lead = null) {
    const paid = detailsAssignedTo(event, personId, lead).reduce((sum, d) => sum + Number(d.amount), 0)
    return paid + personAdminFee(event, personId, lead)
}

// Rows for the organiser payment summary email (Event → Invitees and the events list):
// [{ name, role, package, amount, status, date }], organiser first.
export function paymentSummaryRows(event) {
    const lead = event.lead
    const paidDate = dates => dates.fullPaymentDate || dates.depositDate || null
    const stagHenId = stagHenInviteeId(event)
    return [
        {
            name: lead.first_name + ' ' + (lead.last_name ?? ''),
            role: 'Organiser',
            package: personPackageName(event, lead.id),
            amount: personTotalPaid(event, lead.id),
            status: personPaymentStatus(event, lead.id),
            date: paidDate(personPaymentDates(event, lead.id)),
        },
        ...(event.invitees || []).map(item => ({
            name: item.name,
            role: item.id === stagHenId ? 'Stag/Hen' : null,
            package: personPackageName(event, item.id),
            amount: personTotalPaid(event, item.id),
            status: personPaymentStatus(event, item.id),
            date: paidDate(personPaymentDates(event, item.id)),
        })),
    ]
}

// Headcount for the event's Paid column: the organiser plus every invitee (the actual
// group, not lead.people, which is the quoted group size). full and deposit don't
// overlap — deposit is people who've paid something but not yet in full (Balance Due too).
export function eventPaymentCounts(event) {
    const people = [event.lead?.id, ...(event.invitees || []).map(inv => inv.id)].filter(id => id != null)
    const counts = { total: people.length, full: 0, deposit: 0 }
    people.forEach(id => {
        const status = personPaymentStatus(event, id)
        if (status === 'Paid in Full') counts.full++
        else if (status === 'Paid Deposit' || status === 'Balance Due') counts.deposit++
    })
    return counts
}
